// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
// Heavily inspired by https://www.npmjs.com/package/@azure/communication-react
import React, { useEffect, useRef, useState } from "react";
import { DataProviderPopupList } from "../../DataProviderPopupList/DataProviderPopupList";
import { AddBoxComponent, EMPTY_MESSAGE_REGEX, sanitizeText } from "./components/AddBoxComponent";
import { EditBoxComponent } from "./components/EditBoxComponent";
/**
 * Component for typing and sending messages or comments.
 */
export function SendBox(props) {
    const { disabled, autoFocus, onChange, originalTextValue, setOriginalTextValue, strings, sx, popupProps, mode } = props;
    const [textValue, setTextValue] = useState(originalTextValue ?? "");
    const inputElementRef = useRef(null);
    useEffect(() => {
        setTextValue(originalTextValue ?? "");
    }, [originalTextValue]);
    // Set the cursor to the end of the text box when the component is initially focused (either via autofocus or from tabbing to the element)
    useEffect(() => {
        inputElementRef?.current?.setSelectionRange(originalTextValue?.length ?? 0, originalTextValue?.length ?? 0);
    }, [inputElementRef, originalTextValue]);
    const canPostMessage = (content) => {
        // cant post a message when disabled
        if (disabled) {
            return false;
        }
        // we don't want to send empty messages including spaces, newlines, tabs
        if (EMPTY_MESSAGE_REGEX.test(content)) {
            return false;
        }
        return true;
    };
    const sendMessageOnClick = () => {
        const sanitizedMessage = sanitizeText(textValue);
        if (canPostMessage(sanitizedMessage)) {
            mode === "add" && props.onSendMessage && props.onSendMessage(sanitizedMessage, popupProps?.selectedItems);
            //We've posted the message, so update the original value
            if (setOriginalTextValue) {
                setOriginalTextValue(sanitizedMessage);
            }
            //Clear the selectedItems after posting the message
            if (props.popupProps?.setSelectedItems) {
                props.popupProps.setSelectedItems([]);
            }
            setTextValue("");
        }
    };
    const updateMessageHandler = () => {
        const sanitizedMessage = sanitizeText(textValue);
        if (canPostMessage(sanitizedMessage)) {
            mode === "edit" && props.onUpdateMessage && props.onUpdateMessage(props.messageId, sanitizedMessage, props.popupProps?.selectedItems);
            //We've posted the message, so update the original value
            if (setOriginalTextValue) {
                setOriginalTextValue(sanitizedMessage);
            }
            //Clear the selectedItems after posting the message
            if (props.popupProps?.setSelectedItems) {
                props.popupProps.setSelectedItems([]);
            }
        }
    };
    const cancelEditHandler = () => {
        if (mode !== "edit") {
            return;
        }
        if (props.onCancelEdit) {
            props.onCancelEdit(props.messageId, originalTextValue ?? "", textValue);
        }
        //revert the text to the original value
        setText(originalTextValue ?? "");
        //Revert the selectedItems to empty
        if (props.popupProps?.setSelectedItems) {
            props.popupProps.setSelectedItems([]);
        }
    };
    const setText = (newValue) => {
        if (newValue === undefined) {
            return;
        }
        if (newValue === null) {
            newValue = "";
        }
        setTextValue(newValue);
    };
    return (React.createElement(React.Fragment, null,
        mode === "add" ? (React.createElement(AddBoxComponent, { autoFocus: autoFocus, disabled: disabled, inputRef: inputElementRef, sx: sx, id: "sendbox", placeholderText: strings?.placeholderText, textValue: textValue, onChange: (e) => {
                if (onChange) {
                    onChange(e);
                }
                setText(e?.currentTarget.value);
            }, onKeyDown: (ev) => {
                const keyWasSendingMessage = ev.key === "Enter" && ev.shiftKey === false;
                if (ev.shiftKey && ev.key === "Enter") {
                    ev.preventDefault();
                }
                if (keyWasSendingMessage) {
                    ev.preventDefault();
                    sendMessageOnClick();
                }
            }, sendMessage: sendMessageOnClick })) : (React.createElement(EditBoxComponent, { autoFocus: autoFocus, disabled: disabled, inputRef: inputElementRef, sx: sx, id: "sendbox-edit", placeholderText: strings?.placeholderText, textValue: textValue, onChange: (e) => {
                if (onChange) {
                    onChange(e);
                }
                setText(e?.currentTarget.value);
            }, onKeyDown: (ev, content) => {
                const keyWasSendingMessage = ev.key === "Enter" && ev.shiftKey === false;
                if (ev.shiftKey && ev.key === "Enter") {
                    ev.preventDefault();
                }
                if (keyWasSendingMessage) {
                    ev.preventDefault();
                    updateMessageHandler();
                }
            }, onCancelEdit: cancelEditHandler, onSaveEdit: updateMessageHandler, messageId: props.messageId })),
        popupProps && popupProps.dataProvider && popupProps.popUpChars ? (React.createElement(DataProviderPopupList, { ...popupProps, inputText: textValue, setInputText: setText, inputElementRef: inputElementRef })) : (React.createElement(React.Fragment, null))));
}
